import { flowRight } from 'lodash';
import * as React from 'react';
import * as I18n from 'i18n-js';
import * as elog from '../../elog';
import * as classNames from 'classnames';
import CoreLink from '../../components/core_link';
import { withRouter } from 'react-router';
import { Location } from 'history';
import { buildCSPClick } from '../../elog/mparticle_tracker';
import {
  BrandMetaData,
  ConditionFilter,
  NameMetaData,
  PriceMetaData,
  DescriptionMetaData,
  ImageMetaData,
  ReviewMetaData,
  UrlMetaData,
} from '../lib/metadata_helper';
import SanitizedRender from '../../components/sanitized_render';
import StarRating from '../../components/star_rating';
import { prefilledListingFromCSP } from '../../url_helpers';
import {
  getCategory,
  getSubcategory,
} from '../../csp_category_helper';

import {
  CSP,
} from '../../rql';
import { IUserContext, withUserContext } from '../../components/user_context_provider';
import experiments from '../../experiments';
import { isExperimentEnabled } from '../../user_context_helpers';
import LazyImage from '../../components/lazy_image';

interface IProps extends IUserContext {
  csp?: CSP;
  position?: number;
  conditionFilter?: ConditionFilter;
  inMobileApp?: boolean;
  hideMetadata?: boolean;
  renderAsHorizontal?: boolean;
  shouldLinkToSellForm?: boolean;
  compareCSPCardActive?: boolean;
  location?: Location;
}

const COMPONENT_NAME = 'CspSquareCard';

export class CspSquareCard extends React.Component<IProps, null> {
  priceAndCount() {
    const { conditionFilter, csp } = this.props;
    const { inventory } = csp;

    if (!inventory) return {};

    if (conditionFilter === 'new') {
      const { newLowPrice, newTotal } = inventory;
      return { count: newTotal, price: newLowPrice };
    }

    if (conditionFilter === 'used') {
      const { usedLowPrice, usedTotal } = inventory;
      return { count: usedTotal, price: usedLowPrice };
    }

    const count = inventory.usedTotal + inventory.newTotal;

    if (!inventory.usedLowPrice) {
      return { count, price: inventory.newLowPrice };
    }

    if (!inventory.newLowPrice) {
      return { count, price: inventory.usedLowPrice };
    }

    if (inventory.newLowPrice.amountCents < inventory.usedLowPrice.amountCents) {
      return { count, price: inventory.newLowPrice };
    }

    return { count, price: inventory.usedLowPrice };
  }

  renderCalloutLanguage() {
    if (!this.props.csp) { return null; }

    const { price, count } = this.priceAndCount();

    if (!price && count === 0) { return null; }

    if (!price) {
      elog.error(
        'CSP price is null',
        {
          inventory: JSON.stringify(this.props.csp.inventory),
          conditionFilter: this.props.conditionFilter,
        },
        null,
      );

      return null;
    }

    const t = (key) => {
      return I18n.t(`cms.comparisonShoppingPages.${key}`, { count, price: price.display, tagOpen: '<b>', tagClose: '</b>' });
    };

    if (price.amountCents === 0) {
      return this.props.compareCSPCardActive ? t('comparePriceForFreeCount') : t('priceForFreeHighlightedCount');
    }

    return this.props.compareCSPCardActive ? t('comparePriceFromCount') : t('priceFromWithHighlightedCount');
  }

  renderPlaceholderCard() {
    return (
      <div className="csp-square-card csp-square-card--placeholder">
        <a
          className="csp-square-card__inner"
          href="#"
        >
          <div className="csp-square-card__main">
            <div className="image-box" />
          </div>
          <div className="csp-square-card__details" />
        </a>
      </div>
    );
  }

  destinationPath() {
    if (this.props.shouldLinkToSellForm) {
      return prefilledListingFromCSP(this.props.csp.id);
    }

    if (this.props.conditionFilter === 'new') {
      return this.props.csp.newListingsLink.path;
    }

    if (this.props.conditionFilter === 'used') {
      return this.props.csp.usedListingsLink.path;
    }

    return this.props.csp.webLink.path;
  }

  getDetailsMetadata() {
    if (this.props.hideMetadata) return;

    return {
      itemScope: true,
      itemProp: 'offers',
      itemType: 'http://schema.org/AggregateOffer',
    };
  }

  getItemMetadata() {
    if (this.props.hideMetadata) return;

    return {
      itemScope: true,
      itemType: 'http://schema.org/Product',
    };
  }

  isFilteringCategory() {
    return !!this.props.location.query?.product_type;
  }

  isFilteringSubcategory() {
    return !!this.props.location.query?.category;
  }

  renderCategory() {
    const category = getCategory(this.props.csp);
    const subCategory = getSubcategory(this.props.csp);
    return (
      <div className="csp-square-card__category">
        {!this.isFilteringCategory() && category?.name &&
          <span>
            {category.name}
          </span>
        }
        {!this.isFilteringSubcategory() && subCategory?.name &&
          <span>
            {subCategory.name}
          </span>
        }
      </div>
    );
  }

  renderMetadata() {
    if (this.props.hideMetadata) return;

    return (
      <div>
        <BrandMetaData csp={this.props.csp} />
        <NameMetaData csp={this.props.csp} />
        <DescriptionMetaData csp={this.props.csp} />
        <ImageMetaData csp={this.props.csp} />
        <ReviewMetaData csp={this.props.csp} />
        <UrlMetaData csp={this.props.csp} />
      </div>
    );
  }

  renderPriceMetadata() {
    if (this.props.hideMetadata) return;

    return (
      <PriceMetaData
        csp={this.props.csp}
        conditionFilter={this.props.conditionFilter}
      />
    );
  }

  renderTitle() {
    if (!isExperimentEnabled(this.props.user, experiments.EXPANDED_CSP_TITLE)) {
      return (
        <h3
          className="csp-square-card__title csp-square-card__title--limit-height"
          itemProp="model"
        >
          {this.props.csp.title}
          {this.renderMetadata()}
        </h3>
      );
    }

    return (
      <div
        className="csp-square-card__title"
      >
        <div className="size-90 weight-normal">
          {this.props.csp.brand.name}
        </div>
        <h3
          itemProp="model"
          className="weight-bold"
        >
          {this.props.csp.title.replace(this.props.csp.brand.name, '')}
        </h3>
        {this.renderCategory()}
        {this.renderMetadata()}
      </div>
    );
  }

  renderRating() {
    if (this.props.csp.reviewsCount === 0) return;
    return (
      <div className="mb-half">
        <StarRating
          rating={this.props.csp.averageReviewRating}
          count={this.props.csp.reviewsCount}
          ratingOutOf5
        />
      </div>
    );
  }

  renderFinishIndicator() {
    const multipleFinishes = this.props.csp.finishes?.length > 1;

    if (!multipleFinishes) return;
    return (
      <div className="csp-square-card__finish-indicator">
        {I18n.t('discovery.sellFlow.tiles.multipleFinishes')}
      </div>
    );
  }

  clickEvent() {
    return {
      ...buildCSPClick(this.props.csp),
      displayStyle: COMPONENT_NAME,
      position: this.props.position,
    };
  }

  render() {
    const classes = classNames(
      'csp-square-card',
      { 'csp-square-card--horizontal': this.props.renderAsHorizontal },
    );

    if (!this.props.csp) return this.renderPlaceholderCard();

    return (
      <article
        className={classes}
        {...this.getItemMetadata()}
      >
        <CoreLink
          className="csp-square-card__inner"
          to={this.destinationPath()}
          clickEvent={this.clickEvent()}
          entityId={this.props.csp.id}
          position={this.props.position}
        >
          <div className="csp-square-card__main">
            <LazyImage
              src={this.props.csp.image?.source}
            />
            {this.renderFinishIndicator()}
            {!this.props.renderAsHorizontal && this.renderTitle()}
          </div>
          <div
            className="csp-square-card__details"
          >
            {this.props.renderAsHorizontal && this.renderTitle()}
            <div
              className="csp-square-card__details__price"
              {...this.getDetailsMetadata()}
            >
              {this.renderRating()}
              {this.renderPriceMetadata()}
              <SanitizedRender html={this.renderCalloutLanguage()} />
            </div>
          </div>
        </CoreLink>
      </article>
    );
  }
}

export default flowRight([
  withUserContext,
  withRouter,
])(CspSquareCard);



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/src/cms/components/csp_square_card.tsx